import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, ArrowRight, Sparkles, Loader2, Sun, Moon, Monitor } from "lucide-react";
import { supabase } from "../lib/supabase";
import useTheme from "../components/aura/useTheme";

// ─── Types ─────────────────────────────────────────────────────────────────────

type Mode = "signin" | "signup" | "forgot";

// ─── Theme Toggle ──────────────────────────────────────────────────────────────

const ThemeToggle: React.FC = () => {
  const { mode, setTheme } = useTheme() as any;
  const options = [
    { id: "light", icon: Sun },
    { id: "system", icon: Monitor },
    { id: "dark", icon: Moon },
  ];
  return (
    <div className="flex items-center gap-1 rounded-full border border-panel/15 bg-panel/[0.03] p-1" aria-label="Theme selector">
      {options.map(({ id, icon: Icon }) => (
        <button
          key={id}
          onClick={() => setTheme(id)}
          aria-label={`${id} mode`}
          className={`w-7 h-7 rounded-full grid place-items-center transition-colors ${
            mode === id ? "bg-aura-cyan/15 text-aura-cyan" : "text-whisper-muted hover:text-ink"
          }`}
        >
          <Icon size={13} />
        </button>
      ))}
    </div>
  );
};

// ─── Micro-components ──────────────────────────────────────────────────────────

const Field: React.FC<{
  id: string;
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  icon: React.ReactNode;
  extra?: React.ReactNode;
  testId?: string;
}> = ({ id, label, type, value, onChange, placeholder, icon, extra, testId }) => {
  const [show, setShow] = useState(false);
  const isPassword = type === "password";
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">{label}</label>
        {extra}
      </div>
      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-whisper-muted pointer-events-none">{icon}</span>
        <input
          id={id}
          type={isPassword && show ? "text" : type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testId}
          className="w-full bg-panel/[0.04] border border-panel/15 rounded-2xl pl-10 pr-10 py-3 text-[14px] text-ink placeholder:text-whisper-muted focus:outline-none focus:border-aura-cyan/50 focus:bg-aura-cyan/[0.03] transition-colors"
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-whisper-muted hover:text-ink transition-colors"
            aria-label={show ? "Hide password" : "Show password"}
          >
            {show ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        )}
      </div>
    </div>
  );
};

const ErrBox: React.FC<{ msg: string }> = ({ msg }) => (
  <AnimatePresence>
    {msg && (
      <motion.div
        initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
        className="rounded-2xl border border-red-400/30 bg-red-400/[0.07] px-4 py-3 text-[13px] text-red-300"
        data-testid="auth-error"
      >
        {msg}
      </motion.div>
    )}
  </AnimatePresence>
);

const OkBox: React.FC<{ msg: string }> = ({ msg }) => (
  <AnimatePresence>
    {msg && (
      <motion.div
        initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
        className="rounded-2xl border border-aura-cyan/30 bg-aura-cyan/[0.07] px-4 py-3 text-[13px] text-aura-cyan"
        data-testid="auth-success"
      >
        {msg}
      </motion.div>
    )}
  </AnimatePresence>
);

// ─── Google button ─────────────────────────────────────────────────────────────

const GoogleBtn: React.FC<{ loading: boolean; onClick: () => void }> = ({ loading, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-2xl border border-panel/15 bg-panel/[0.03] hover:bg-panel/[0.07] hover:border-panel/25 transition-colors text-[14px] text-ink disabled:opacity-60 disabled:cursor-not-allowed"
    data-testid="auth-google"
  >
    {loading ? (
      <Loader2 size={16} className="animate-spin" />
    ) : (
      <svg width="18" height="18" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M47.5 24.5c0-1.6-.15-3.14-.42-4.63H24v8.76h13.18c-.57 3.07-2.3 5.67-4.9 7.41v6.16h7.93C44.18 38.18 47.5 31.84 47.5 24.5z" fill="#4285F4"/>
        <path d="M24 48c6.48 0 11.93-2.15 15.9-5.81l-7.93-6.16c-2.15 1.44-4.9 2.3-7.97 2.3-6.13 0-11.32-4.14-13.18-9.7H2.64v6.35C6.59 43.01 14.73 48 24 48z" fill="#34A853"/>
        <path d="M10.82 28.63A14.47 14.47 0 0 1 9.5 24c0-1.6.28-3.15.79-4.63V13.02H2.64A24.04 24.04 0 0 0 0 24c0 3.88.93 7.56 2.64 10.98l8.18-6.35z" fill="#FBBC04"/>
        <path d="M24 9.5c3.45 0 6.55 1.19 8.99 3.5l6.74-6.74C35.93 2.38 30.47 0 24 0 14.73 0 6.59 4.99 2.64 13.02l8.18 6.35C12.68 13.64 17.87 9.5 24 9.5z" fill="#EA4335"/>
      </svg>
    )}
    Continue with Google
  </button>
);

const getAuthCallbackUrl = () => {
  const configuredUrl = process.env.REACT_APP_PUBLIC_SITE_URL?.replace(/\/$/, "");
  if (configuredUrl) return `${configuredUrl}/auth/callback`;

  const { origin, hostname } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "https://auraspeak.in/auth/callback";
  }

  return `${origin}/auth/callback`;
};

// ─── Divider ──────────────────────────────────────────────────────────────────

const Divider: React.FC = () => (
  <div className="flex items-center gap-3">
    <div className="flex-1 h-px bg-panel/12" />
    <span className="font-mono text-[9.5px] tracking-[0.22em] uppercase text-whisper-muted">or</span>
    <div className="flex-1 h-px bg-panel/12" />
  </div>
);

// ─── Main AuthPage ─────────────────────────────────────────────────────────────

const AuthPage: React.FC = () => {
  const nav = useNavigate();
  const [mode, setMode] = useState<Mode>("signin");

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  // If already logged in → redirect to dashboard or onboarding
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      if (!data.session) return;
      // Check if they've completed onboarding (have a display_name)
      const { data: profile } = await supabase
        .from("users")
        .select("display_name")
        .eq("id", data.session.user.id)
        .maybeSingle();
      if (profile?.display_name) {
        nav("/dashboard", { replace: true });
      } else {
        nav("/onboarding", { replace: true });
      }
    });
  }, [nav]);

  const clearFeedback = () => { setError(""); setOk(""); };

  // ── Google OAuth ─────────────────────────────────────────────────────────────
  const handleGoogle = async () => {
    clearFeedback();
    setGoogleLoading(true);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: getAuthCallbackUrl(),
        queryParams: { prompt: "select_account" }
      },
    });
    if (err) { setError(err.message); setGoogleLoading(false); }
    // Supabase will redirect the browser — no need to setLoading(false)
  };

  // ── Sign In ──────────────────────────────────────────────────────────────────
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    clearFeedback();
    if (!email || !password) { setError("Please fill in all fields."); return; }
    setLoading(true);
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (err) { setError(err.message); return; }

    // Check if onboarding is complete
    const { data: profile } = await supabase
      .from("users")
      .select("display_name")
      .eq("id", data.user!.id)
      .maybeSingle();
    if (profile?.display_name) {
      nav("/dashboard", { replace: true });
    } else {
      nav("/onboarding", { replace: true });
    }
  };

  // ── Sign Up ──────────────────────────────────────────────────────────────────
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    clearFeedback();
    if (!email || !password) { setError("Please fill in all fields."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setLoading(true);

    const { data, error: err } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName || email.split("@")[0] },
      },
    });
    setLoading(false);

    if (err) { setError(err.message); return; }

    if (data.session) {
      // Email confirmation disabled — user is immediately active
      // Create a stub row so they can't bypass onboarding
      await supabase.from("users").upsert({
        id: data.user!.id,
        target_language: "",      // Will be filled by onboarding
        native_language: "",
        learning_goal: "",
        cefr_level: "A1",
        claimed_cefr_level: null,
        effective_cefr_level: "A1",
        placement_status: "needs_onboarding",
        last_assessed_at: null,
        assessment_completed_at: null,
        streak: 0,
      }, { onConflict: "id" });

      nav("/onboarding", { replace: true });
    } else {
      setOk("Check your inbox — we sent a confirmation link. Once confirmed, sign in here.");
      setMode("signin");
    }
  };

  // ── Forgot Password ──────────────────────────────────────────────────────────
  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    clearFeedback();
    if (!email) { setError("Enter your email address."); return; }
    setLoading(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth`,
    });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setOk("Password reset link sent. Check your email.");
  };

  const handleSubmit = mode === "signin" ? handleSignIn : mode === "signup" ? handleSignUp : handleForgot;

  return (
    <div className="relative min-h-screen bg-base text-ink overflow-hidden flex flex-col px-4" data-testid="page-auth">
      {/* Ambient glows */}
      <div aria-hidden className="pointer-events-none fixed -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-aura-cyan/10 blur-[160px]" />
      <div aria-hidden className="pointer-events-none fixed -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-aura-violet/12 blur-[160px]" />

      {/* Header bar */}
      <div className="relative z-10 flex items-center justify-between py-5 max-w-[440px] mx-auto w-full">
        <Link to="/" className="flex items-center gap-3 group" data-testid="auth-logo">
          <span className="w-2.5 h-2.5 rounded-full bg-aura-cyan shadow-[0_0_14px_rgb(var(--accent-cyan-rgb)/0.9)]" />
          <span className="font-display text-[20px] tracking-[-0.02em] text-ink font-light">
            AURA<span className="text-aura-cyan">.</span>
          </span>
        </Link>
        <ThemeToggle />
      </div>

      {/* Center card */}
      <div className="relative z-10 flex-1 flex items-center justify-center py-10">
        <div className="w-full max-w-[440px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-3xl glass edge-glow overflow-hidden"
            >
              <div aria-hidden className="absolute -top-20 -right-12 w-[280px] h-[280px] rounded-full bg-aura-cyan/15 blur-[100px] pointer-events-none" />

              <div className="relative z-10 p-8">
                {/* Header */}
                <div>
                  <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">
                    <Sparkles size={11} />
                    {mode === "signin" ? "Welcome back" : mode === "signup" ? "Create your account" : "Reset password"}
                  </div>
                  <h1 className="mt-2 font-display text-[26px] font-light tracking-tight text-ink">
                    {mode === "signin"
                      ? "Pick up where you left off."
                      : mode === "signup"
                      ? "AURA is ready to remember you."
                      : "We'll send a reset link."}
                  </h1>
                </div>

                {/* Google OAuth — only for signin + signup */}
                {mode !== "forgot" && (
                  <div className="mt-6 space-y-3">
                    <GoogleBtn loading={googleLoading} onClick={handleGoogle} />
                    <Divider />
                  </div>
                )}

                {/* Email/password form */}
                <form onSubmit={handleSubmit} className={`${mode !== "forgot" ? "mt-0" : "mt-7"} space-y-4`} noValidate>
                  {mode === "signup" && (
                    <Field
                      id="auth-name"
                      label="First name"
                      type="text"
                      value={displayName}
                      onChange={setDisplayName}
                      placeholder="Your first name"
                      icon={<Sparkles size={15} />}
                      testId="auth-name"
                    />
                  )}

                  <Field
                    id="auth-email"
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(v) => { setEmail(v); clearFeedback(); }}
                    placeholder="you@example.com"
                    icon={<Mail size={15} />}
                    testId="auth-email"
                  />

                  {mode !== "forgot" && (
                    <Field
                      id="auth-password"
                      label="Password"
                      type="password"
                      value={password}
                      onChange={(v) => { setPassword(v); clearFeedback(); }}
                      placeholder={mode === "signup" ? "At least 8 characters" : "••••••••"}
                      icon={<Lock size={15} />}
                      testId="auth-password"
                      extra={
                        mode === "signin" ? (
                          <button
                            type="button"
                            onClick={() => { setMode("forgot"); clearFeedback(); }}
                            className="font-mono text-[9.5px] tracking-[0.18em] uppercase text-whisper-muted hover:text-aura-cyan transition-colors"
                          >
                            Forgot?
                          </button>
                        ) : undefined
                      }
                    />
                  )}

                  <ErrBox msg={error} />
                  <OkBox msg={ok} />

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full justify-center py-3 text-[13px] mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="auth-submit"
                  >
                    {loading ? (
                      <Loader2 size={15} className="animate-spin" />
                    ) : mode === "signin" ? (
                      <>Continue <ArrowRight size={14} /></>
                    ) : mode === "signup" ? (
                      <>Create account <ArrowRight size={14} /></>
                    ) : (
                      <>Send reset link <ArrowRight size={14} /></>
                    )}
                  </button>
                </form>

                {/* Mode switcher */}
                <div className="mt-6 pt-5 border-t border-panel/8 flex items-center justify-center gap-2 text-[13px]">
                  {mode === "signin" && (
                    <>
                      <span className="text-whisper">New to AURA?</span>
                      <button
                        onClick={() => { setMode("signup"); clearFeedback(); }}
                        className="text-aura-cyan hover:text-ink transition-colors font-medium"
                        data-testid="auth-switch-signup"
                      >
                        Create account
                      </button>
                    </>
                  )}
                  {mode === "signup" && (
                    <>
                      <span className="text-whisper">Already have an account?</span>
                      <button
                        onClick={() => { setMode("signin"); clearFeedback(); }}
                        className="text-aura-cyan hover:text-ink transition-colors font-medium"
                        data-testid="auth-switch-signin"
                      >
                        Sign in
                      </button>
                    </>
                  )}
                  {mode === "forgot" && (
                    <button
                      onClick={() => { setMode("signin"); clearFeedback(); }}
                      className="text-whisper hover:text-ink transition-colors"
                      data-testid="auth-back-signin"
                    >
                      ← Back to sign in
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <p className="mt-6 text-center font-mono text-[10px] tracking-[0.18em] uppercase text-whisper-muted">
            Your data is private · AURA never sells it
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
